import httpClient from './httpClient';

class DashboardService {
  // Get comprehensive dashboard summary with all key metrics
  async getDashboardSummary() {
    try {
      const response = await httpClient.get('/api/reports/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  }

  // Get detailed inventory summary
  async getInventorySummary() {
    try {
      const response = await httpClient.get('/api/inventory/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory summary:', error);
      throw error;
    }
  }

  // Get recent quotations with optional limit
  async getRecentQuotations(limit = 5) {
    try {
      const response = await httpClient.get(`/api/quotations?limit=${limit}&sort=created_at&order=desc`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recent quotations:', error);
      throw error;
    }
  }

  // Get recent sales orders with optional limit  
  async getRecentSales(limit = 5) {
    try {
      const response = await httpClient.get(`/api/sales?limit=${limit}&sort=order_date&order=desc`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recent sales:', error);
      throw error;
    }
  }

  // Get sales trends for specified period
  async getSalesTrends(startDate, endDate) {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      const response = await httpClient.get(`/api/reports/sales?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales trends:', error);
      throw error;
    }
  }

  // Get inventory trends and alerts
  async getInventoryTrends() {
    try {
      const response = await httpClient.get('/api/reports/inventory');
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory trends:', error);
      throw error;
    }
  }

  // Get quotation pipeline and conversion metrics
  async getQuotationStats() {
    try {
      const response = await httpClient.get('/api/quotations/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching quotation stats:', error);
      throw error;
    }
  }

  // Get sales performance statistics
  async getSalesStats() {
    try {
      const response = await httpClient.get('/api/sales/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching sales stats:', error);
      throw error;
    }
  }

  // Get product statistics
  async getProductStats() {
    try {
      const response = await httpClient.get('/api/products/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching product stats:', error);
      throw error;
    }
  }

  // Get role-specific dashboard data
  async getRoleBasedMetrics(userRole) {
    try {
      const baseData = await this.getDashboardSummary();
      
      switch (userRole) {
        case 'admin':
          return this.getAdminMetrics(baseData);
        case 'warehouse_manager':
          return this.getWarehouseMetrics(baseData);
        case 'sales_person':
          return this.getSalesPersonMetrics(baseData);
        default:
          return this.getDefaultMetrics(baseData);
      }
    } catch (error) {
      console.error('Error fetching role-based metrics:', error);
      throw error;
    }
  }

  // Admin-specific dashboard metrics
  async getAdminMetrics(baseData) {
    try {
      const [productStats, quotationStats, salesStats] = await Promise.all([
        this.getProductStats(),
        this.getQuotationStats(),
        this.getSalesStats()
      ]);

      return {
        ...baseData,
        systemOverview: {
          totalUsers: productStats.total_users || 0,
          activeUsers: productStats.active_users || 0,
          systemUptime: '99.9%',
          storageUsed: productStats.storage_used || '45%'
        },
        businessMetrics: {
          totalRevenue: salesStats.total_revenue || 0,
          profitMargin: salesStats.profit_margin || 0,
          conversionRate: quotationStats.conversion_rate || 0,
          customerSatisfaction: salesStats.customer_satisfaction || 0
        }
      };
    } catch (error) {
      console.error('Error fetching admin metrics:', error);
      return baseData;
    }
  }

  // Warehouse manager-specific dashboard metrics
  async getWarehouseMetrics(baseData) {
    try {
      const [inventoryTrends] = await Promise.all([
        this.getInventoryTrends()
      ]);

      return {
        ...baseData,
        warehouseOperations: {
          receivingSchedule: inventoryTrends.receiving_schedule || [],
          shippingSchedule: inventoryTrends.shipping_schedule || [],
          storageUtilization: inventoryTrends.storage_utilization || 75,
          pickingEfficiency: inventoryTrends.picking_efficiency || 85
        },
        inventoryAlerts: {
          criticalStock: inventoryTrends.critical_stock || [],
          expiringSoon: inventoryTrends.expiring_soon || [],
          overstock: inventoryTrends.overstock || []
        }
      };
    } catch (error) {
      console.error('Error fetching warehouse metrics:', error);
      return baseData;
    }
  }

  // Sales person-specific dashboard metrics
  async getSalesPersonMetrics(baseData) {
    try {
      const [quotationStats, salesStats, recentQuotations] = await Promise.all([
        this.getQuotationStats(),
        this.getSalesStats(),
        this.getRecentQuotations(10)
      ]);

      return {
        ...baseData,
        salesPipeline: {
          quotationsInProgress: quotationStats.in_progress || 0,
          quotationsPending: quotationStats.pending || 0,
          quotationsConverted: quotationStats.converted || 0,
          averageConversionTime: quotationStats.avg_conversion_time || 0
        },
        customerInteractions: {
          recentQuotations: recentQuotations.quotations || [],
          followUpRequired: quotationStats.follow_up_required || [],
          hotLeads: quotationStats.hot_leads || []
        },
        salesTargets: {
          monthlyTarget: salesStats.monthly_target || 0,
          monthlyActual: salesStats.monthly_actual || 0,
          quarterlyTarget: salesStats.quarterly_target || 0,
          quarterlyActual: salesStats.quarterly_actual || 0
        }
      };
    } catch (error) {
      console.error('Error fetching sales person metrics:', error);
      return baseData;
    }
  }

  // Default metrics for other roles
  async getDefaultMetrics(baseData) {
    return {
      ...baseData,
      limitedAccess: true
    };
  }

  // Get alert notifications for current user
  async getAlerts(userRole) {
    try {
      const alerts = [];
      const dashboardData = await this.getDashboardSummary();

      // Common alerts for all roles
      if (dashboardData.inventory && dashboardData.inventory.low_stock_count > 0) {
        alerts.push({
          type: 'warning',
          title: '低库存警告',
          message: `${dashboardData.inventory.low_stock_count} 个商品库存不足`,
          action: '/inventory',
          priority: 'high'
        });
      }

      if (dashboardData.inventory && dashboardData.inventory.out_of_stock_count > 0) {
        alerts.push({
          type: 'error',
          title: '缺货警告',
          message: `${dashboardData.inventory.out_of_stock_count} 个商品已缺货`,
          action: '/inventory',
          priority: 'critical'
        });
      }

      // Role-specific alerts
      if (userRole === 'sales_person' || userRole === 'admin') {
        const quotationStats = await this.getQuotationStats();
        if (quotationStats.overdue_quotations > 0) {
          alerts.push({
            type: 'warning',
            title: '逾期报价',
            message: `${quotationStats.overdue_quotations} 个报价已逾期`,
            action: '/quotations',
            priority: 'medium'
          });
        }
      }

      return alerts;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }
  }

  // Format currency values
  formatCurrency(value, currency = 'CNY') {
    if (typeof value !== 'number') return '¥0';
    
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  // Format percentage values
  formatPercentage(value, decimals = 1) {
    if (typeof value !== 'number') return '0%';
    return `${value.toFixed(decimals)}%`;
  }

  // Calculate trend direction and color
  getTrendIndicator(current, previous) {
    if (!previous || previous === 0) return { trend: 'neutral', change: '0%', color: 'text.secondary' };
    
    const change = ((current - previous) / previous) * 100;
    const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
    const color = change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary';
    
    return {
      trend,
      change: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
      color
    };
  }
}

const dashboardService = new DashboardService();
export default dashboardService;